import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import { useStudentPortal } from '../store/StudentPortalContext';
import { useSettings } from '../store/SettingsContext';
import { useDisplaySettings } from '../store/DisplaySettingsContext';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import AssignmentService from '../services/AssignmentService';
import ExternalMessageService from '../services/ExternalMessageService';
import JournalService from '../services/JournalService';
import PointsService from '../services/PointsService';
import { formatDate } from '../utils/dateFormat';

const extractField = (data, field) => {
  if (!field) return null;
  const parts = field.split('.');
  let current = data;
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return null;
    }
  }
  return current;
};

const buildQuote = (payload, quoteField, authorField) => {
  if (!payload) return null;
  const data = Array.isArray(payload) ? payload[0] : payload;
  const htmlQuote = data?.h || null;
  const quoteText =
    extractField(data, quoteField) ||
    data?.content ||
    data?.quote ||
    data?.text ||
    data?.q ||
    (htmlQuote ? String(htmlQuote).replace(/<[^>]+>/g, '').trim() : null) ||
    null;
  const author =
    extractField(data, authorField) ||
    data?.author ||
    data?.a ||
    null;
  if (!quoteText) return null;
  return { text: String(quoteText), author: author ? String(author) : '' };
};

const fallbackQuotes = [
  { text: 'Small steps each day add up to big changes.', author: 'SafeguardPro' },
  { text: 'Progress over perfection.', author: 'SafeguardPro' },
  { text: "You're allowed to take up space and ask for support.", author: 'SafeguardPro' },
  { text: 'Showing up matters. Keep going.', author: 'SafeguardPro' },
  { text: "Be kind to yourself - you're doing your best.", author: 'SafeguardPro' },
  { text: 'Every day is a fresh start.', author: 'SafeguardPro' },
  { text: 'You are capable of more than you think.', author: 'SafeguardPro' },
  { text: 'Asking for help is a strength, not a weakness.', author: 'SafeguardPro' },
  { text: 'Focus on what you can control today.', author: 'SafeguardPro' },
  { text: 'Small wins are still wins.', author: 'SafeguardPro' }
];

const pickFallbackQuote = () => fallbackQuotes[Math.floor(Math.random() * fallbackQuotes.length)];

export default function StudentPortalDashboardPage() {
  const { student } = useStudentPortal();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { settings: displaySettings } = useDisplaySettings();
  const portalSettings = settings?.studentPortal || {};
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const assignmentService = useMemo(() => new AssignmentService(), []);
  const messageService = useMemo(() => new ExternalMessageService(), []);
  const journalService = useMemo(() => new JournalService(), []);
  const pointsService = useMemo(() => new PointsService(), []);
  const [activeTab, setActiveTab] = useState('overview');
  const [quote, setQuote] = useState(null);
  const [quoteStatus, setQuoteStatus] = useState('idle');
  const [quoteError, setQuoteError] = useState('');

  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const activities = useMemo(() => activityService.list(), [activityService]);

  const enrolledProgrammes = useMemo(() => {
    if (!student) return [];
    const programmeNames = new Set((student.enrolments || []).map((enrol) => enrol.programme));
    return programmes.filter((programme) => programmeNames.has(programme.name));
  }, [programmes, student]);

  const enrolledActivities = useMemo(() => {
    if (!student) return [];
    const activityIds = new Set((student.activities || []).map((activity) => activity.activityId || activity.id));
    return activities.filter((activity) => activityIds.has(activity.id));
  }, [activities, student]);

  const assignments = useMemo(
    () => (student ? assignmentService.listForStudents([student.id]) : []),
    [assignmentService, student]
  );

  const messages = useMemo(
    () => (student ? messageService.listForStudent(student.id) : []),
    [messageService, student]
  );

  const journalEntries = useMemo(
    () => (student ? journalService.listForStudent(student.id) : []),
    [journalService, student]
  );

  const wellbeingEntries = useMemo(
    () => journalEntries.filter((entry) => entry.category === 'wellbeing'),
    [journalEntries]
  );

  const wellbeingSettings = portalSettings.wellbeing || {};
  const wellbeingEnabled = Boolean(wellbeingSettings.enabled);
  const wellbeingFrequencyDays = Number(wellbeingSettings.frequencyDays || 7);
  const lastWellbeingEntry = wellbeingEntries.length
    ? wellbeingEntries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
    : null;
  const needsCheckin = wellbeingEnabled
    ? lastWellbeingEntry
      ? new Date(lastWellbeingEntry.createdAt) < new Date(Date.now() - wellbeingFrequencyDays * 24 * 60 * 60 * 1000)
      : true
    : false;

  const totalPoints = student ? pointsService.getTotalForStudent(student.id) : 0;
  const rewards = Array.isArray(portalSettings?.rewards) ? portalSettings.rewards : [];
  const nextReward = rewards
    .filter((reward) => Number(reward.points) > totalPoints)
    .sort((a, b) => Number(a.points) - Number(b.points))[0];
  const pointsScope = portalSettings.pointsScope || 'all';
  const pointsProgrammeIds = portalSettings.pointsProgrammeIds || [];
  const pointsActivityIds = portalSettings.pointsActivityIds || [];
  const programmeNameLookup = new Set(
    pointsProgrammeIds.map((id) => programmes.find((programme) => programme.id === id)?.name || id)
  );
  const studentProgrammeNames = new Set((student?.enrolments || []).map((enrol) => enrol.programme));
  const studentActivityIds = new Set((student?.activities || []).map((activity) => activity.activityId || activity.id));
  const pointsActiveForStudent = Boolean(portalSettings.pointsEnabled) && (
    pointsScope === 'all' ||
    (pointsScope === 'programmes' && (pointsProgrammeIds.length === 0
      ? true
      : [...studentProgrammeNames].some((name) => programmeNameLookup.has(name)))) ||
    (pointsScope === 'activities' && (pointsActivityIds.length === 0
      ? true
      : pointsActivityIds.some((id) => studentActivityIds.has(id))))
  );
  const quoteMode = portalSettings.quoteMode || (portalSettings.quoteApiEnabled ? 'fallback' : 'off');
  const localQuotes = Array.isArray(portalSettings.localQuotes) && portalSettings.localQuotes.length
    ? portalSettings.localQuotes
    : fallbackQuotes;
  const pickLocalQuote = useCallback(() => {
    if (!localQuotes.length) return pickFallbackQuote();
    return localQuotes[Math.floor(Math.random() * localQuotes.length)];
  }, [localQuotes]);

  const upcomingLessons = useMemo(() => {
    const combined = [
      ...enrolledProgrammes.map((programme) => ({
        id: programme.id,
        title: programme.name,
        date: programme.startDate || '',
        schedule: `${programme.day || programme.days?.join(', ') || 'TBD'} · ${programme.time || 'TBD'}`
      })),
      ...enrolledActivities.map((activity) => ({
        id: activity.id,
        title: activity.name,
        date: activity.date,
        schedule: `${formatDate(activity.date)} · ${activity.time || 'TBD'}`
      }))
    ];
    return combined.slice(0, 4);
  }, [enrolledProgrammes, enrolledActivities]);

  const notifications = useMemo(
    () => messages.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [messages]
  );

  const attendanceTrend = useMemo(() => {
    const trend = student?.attendance?.last8Weeks || [];
    if (!trend.length) return [];
    return trend.map((value, idx) => ({
      week: `W${idx + 1}`,
      value
    }));
  }, [student]);

  const assignmentStats = useMemo(() => {
    const now = new Date();
    let submitted = 0;
    let overdue = 0;
    let pending = 0;
    assignments.forEach((assignment) => {
      const submissions = (assignment.timeline || [])
        .filter((entry) => entry.type === 'submission' && entry.studentId === student?.id)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (submissions.length > 0) {
        submitted += 1;
        return;
      }
      const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;
      if (dueDate && dueDate < now) {
        overdue += 1;
      } else {
        pending += 1;
      }
    });
    return [
      { name: 'Submitted', value: submitted, color: '#34c759' },
      { name: 'Pending', value: pending, color: '#ff9f0a' },
      { name: 'Overdue', value: overdue, color: '#ff453a' }
    ];
  }, [assignments, student]);

  const chartAnimation = !displaySettings.reducedMotion;

  useEffect(() => {
    if (quoteMode === 'off') {
      setQuote(null);
      setQuoteStatus('idle');
      setQuoteError('');
      return;
    }
    if (quoteMode === 'featured') {
      setQuote(pickLocalQuote());
      setQuoteStatus('ready');
      setQuoteError('');
      return;
    }
    if (!portalSettings?.quoteApiUrl) {
      if (quoteMode === 'fallback') {
        setQuote(pickLocalQuote());
        setQuoteStatus('fallback');
        setQuoteError('Quote API URL is missing.');
      } else {
        setQuote(null);
        setQuoteStatus('error');
        setQuoteError('Quote API URL is missing.');
      }
      return;
    }
    let active = true;
    const controller = new AbortController();
    const fetchQuote = async () => {
      setQuoteStatus('loading');
      setQuoteError('');
      try {
        let headers = {};
        if (portalSettings.quoteApiHeaders?.trim()) {
          headers = JSON.parse(portalSettings.quoteApiHeaders);
        }
        const response = await fetch(portalSettings.quoteApiUrl, { headers, signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Quote API error: ${response.status}`);
        }
        const data = await response.json();
        const parsed = buildQuote(data, portalSettings.quoteField, portalSettings.authorField);
        if (!active) return;
        if (!parsed) {
          if (quoteMode === 'fallback') {
            const fallback = pickLocalQuote();
            setQuote(fallback);
            setQuoteStatus('fallback');
            setQuoteError('Quote data could not be parsed.');
            return;
          }
          setQuote(null);
          setQuoteStatus('error');
          setQuoteError('Quote data could not be parsed.');
          return;
        }
        setQuote(parsed);
        setQuoteStatus('ready');
      } catch (error) {
        if (!active) return;
        if (quoteMode === 'fallback') {
          const fallback = pickLocalQuote();
          setQuote(fallback);
          setQuoteStatus('fallback');
          setQuoteError('Unable to load quote.');
        } else {
          setQuote(null);
          setQuoteStatus('error');
          setQuoteError('Unable to load quote.');
        }
      }
    };
    fetchQuote();
    return () => {
      active = false;
      controller.abort();
    };
  }, [
    quoteMode,
    portalSettings?.quoteApiUrl,
    portalSettings?.quoteApiHeaders,
    portalSettings?.quoteField,
    portalSettings?.authorField,
    pickLocalQuote
  ]);

  const renderQuote = () => {
    if (quoteMode === 'off') return null;
    if (quoteStatus === 'loading') {
      return <div className="text-sm sp-muted">Loading today’s quote…</div>;
    }
    if (quoteStatus === 'error') {
      return (
        <div className="text-sm sp-muted">
          {quoteError || 'Quote not available right now.'}
        </div>
      );
    }
    if (quoteStatus === 'fallback' && quote) {
      return (
        <>
          <div className="text-sm sp-muted">"{quote.text}"</div>
          {quote.author && <div className="text-xs sp-muted mt-2">- {quote.author}</div>}
          <div className="text-xs sp-muted mt-2">Using offline quote while the API is unavailable.</div>
        </>
      );
    }
    if (quote) {
      return (
        <>
          <div className="text-sm sp-muted">"{quote.text}"</div>
          {quote.author && <div className="text-xs sp-muted mt-2">- {quote.author}</div>}
        </>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Student Dashboard</h2>
        <p className="sp-muted">Track your progress, rewards, and support in one place.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {['overview', 'attendance', 'assignments', 'notifications'].map((tab) => (
          <Button
            key={tab}
            size="sm"
            variant={activeTab === tab ? 'secondary' : 'ghost'}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="sp-portal-hero sp-portal-glow">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="sp-portal-chip">Level up your progress</div>
                <h3 className="sp-value" style={{ fontSize: '22px', fontWeight: 700, marginTop: '10px' }}>
                  Hey {student?.name?.split(' ')[0] || 'there'}!
                </h3>
                <p className="text-sm sp-muted">
                  Keep your momentum going with check-ins, rewards, and progress tracking.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {wellbeingEnabled && (
                  <Button size="sm" variant="secondary" onClick={() => navigate('/student-portal/journal', { state: { category: 'wellbeing' } })}>
                    Wellbeing check-in
                  </Button>
                )}
                {pointsActiveForStudent && (
                  <Button size="sm" variant="secondary" onClick={() => navigate('/student-portal/rewards')}>
                    View rewards
                  </Button>
                )}
                <Button size="sm" onClick={() => navigate('/student-portal/timetable')}>
                  Timetable
                </Button>
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-3 mt-4">
              <Card className="sp-portal-card sp-portal-kpi">
                <div className="text-xs sp-muted">Attendance</div>
                <div className="sp-value" style={{ fontSize: '20px', fontWeight: 700 }}>
                  {student?.attendance?.rate || 0}%
                </div>
              </Card>
              <Card className="sp-portal-card sp-portal-kpi">
                <div className="text-xs sp-muted">Assignments</div>
                <div className="sp-value" style={{ fontSize: '20px', fontWeight: 700 }}>
                  {assignments.length}
                </div>
              </Card>
              <Card className="sp-portal-card sp-portal-kpi">
                <div className="text-xs sp-muted">Notifications</div>
                <div className="sp-value" style={{ fontSize: '20px', fontWeight: 700 }}>
                  {notifications.length}
                </div>
              </Card>
              <Card className="sp-portal-card sp-portal-kpi">
                <div className="text-xs sp-muted">Points</div>
                <div className="sp-value" style={{ fontSize: '20px', fontWeight: 700 }}>
                  {pointsActiveForStudent ? `${totalPoints} pts` : 'Off'}
                </div>
              </Card>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Card className="sp-portal-card">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Upcoming lessons</h3>
              <div className="mt-3 space-y-2">
                {upcomingLessons.length === 0 ? (
                  <p className="text-sm sp-muted">No sessions scheduled yet.</p>
                ) : (
                  upcomingLessons.map((lesson) => (
                    <div key={lesson.id} className="sp-panel sp-panel--muted p-3">
                      <div className="sp-value" style={{ fontWeight: 600 }}>{lesson.title}</div>
                      <div className="text-xs sp-muted">{lesson.schedule}</div>
                    </div>
                  ))
                )}
              </div>
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => navigate('/student-portal/timetable')}>
                Open timetable
              </Button>
            </Card>
            <Card className="sp-portal-card">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Assignments</h3>
              <div className="mt-3 space-y-2">
                {assignments.length === 0 ? (
                  <p className="text-sm sp-muted">No assignments yet.</p>
                ) : (
                  assignments.slice(0, 3).map((assignment) => (
                    <div key={assignment.id} className="sp-panel sp-panel--muted p-3">
                      <div className="sp-value" style={{ fontWeight: 600 }}>{assignment.title}</div>
                      <div className="text-xs sp-muted">
                        Due {assignment.dueDate ? formatDate(assignment.dueDate) : 'No due date'}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => setActiveTab('assignments')}>
                View assignments
              </Button>
            </Card>
            <Card className="sp-portal-card">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Notifications</h3>
              <div className="mt-3 space-y-2">
                {notifications.length === 0 ? (
                  <p className="text-sm sp-muted">No notifications yet.</p>
                ) : (
                  notifications.slice(0, 3).map((notice) => (
                    <div key={notice.id} className="sp-panel sp-panel--muted p-3">
                      <div className="sp-value" style={{ fontWeight: 600 }}>{notice.subject}</div>
                      <div className="text-xs sp-muted">{formatDate(notice.createdAt)}</div>
                    </div>
                  ))
                )}
              </div>
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => setActiveTab('notifications')}>
                View notifications
              </Button>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="sp-portal-card">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Progress insights</h3>
              <div className="mt-3 sp-chart-container">
                {attendanceTrend.length === 0 ? (
                  <p className="text-sm sp-muted">No attendance trend data yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={attendanceTrend} margin={{ left: -20, right: 12 }}>
                      <CartesianGrid strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.2)" />
                      <XAxis dataKey="week" tick={{ fill: 'var(--sp-text-secondary)', fontSize: 12 }} />
                      <YAxis tick={{ fill: 'var(--sp-text-secondary)', fontSize: 12 }} domain={[0, 100]} />
                      <Tooltip
                        contentStyle={{ background: 'var(--sp-surface)', borderColor: 'var(--sp-border)' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#4ea1ff"
                        strokeWidth={3}
                        dot={{ r: 3 }}
                        isAnimationActive={chartAnimation}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="sp-chart-caption mt-3">Attendance trend for the last 8 weeks.</div>
            </Card>
            <Card className="sp-portal-card">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Assignment status</h3>
              <div className="mt-3 sp-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={assignmentStats} layout="vertical" margin={{ left: 12, right: 12 }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="name" type="category" tick={{ fill: 'var(--sp-text-secondary)', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ background: 'var(--sp-surface)', borderColor: 'var(--sp-border)' }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 6, 6]} isAnimationActive={chartAnimation}>
                      {assignmentStats.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="sp-chart-caption mt-3">
                Submitted {assignmentStats[0]?.value || 0} · Pending {assignmentStats[1]?.value || 0} · Overdue {assignmentStats[2]?.value || 0}
              </div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {wellbeingEnabled && (
              <Card className={`sp-portal-card ${needsCheckin ? 'sp-panel--critical' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="sp-value" style={{ fontWeight: 600 }}>
                    {wellbeingSettings.promptTitle || 'Wellbeing check-in'}
                  </h3>
                  {needsCheckin && <Badge variant="warning">Due</Badge>}
                </div>
                <p className="text-sm sp-muted mt-2">
                  {wellbeingSettings.promptText ||
                    'Keep a short note on how you are feeling, and share it with your support team if needed.'}
                </p>
                {lastWellbeingEntry && (
                  <div className="text-xs sp-muted mt-2">
                    Last check-in {formatDate(lastWellbeingEntry.createdAt)}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-3"
                  onClick={() => navigate('/student-portal/journal', { state: { category: 'wellbeing' } })}
                >
                  Start check-in
                </Button>
              </Card>
            )}
            {pointsActiveForStudent && (
              <Card className="sp-portal-card">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="sp-value" style={{ fontWeight: 600 }}>Your points</h3>
                  <Badge variant="active">{totalPoints} pts</Badge>
                </div>
                <p className="text-sm sp-muted mt-2">
                  {nextReward
                    ? `Next reward at ${nextReward.points} points: ${nextReward.reward}`
                    : 'You have unlocked all current rewards.'}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-3"
                  onClick={() => navigate('/student-portal/rewards')}
                >
                  View rewards
                </Button>
              </Card>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="sp-portal-card">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Motivation</h3>
              <div className="mt-3">{renderQuote()}</div>
              {portalSettings?.quoteApiEnabled && portalSettings?.quoteApiUrl?.includes('zenquotes.io') && (
                <div className="text-xs sp-muted mt-3">Quotes provided by ZenQuotes API.</div>
              )}
            </Card>
            <Card className="sp-portal-card">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Support & wellbeing</h3>
              <p className="text-sm sp-muted mt-2">Helpful resources and support links.</p>
              <div className="mt-3 space-y-2 text-sm">
                <a className="sp-link" href="https://www.nhs.uk/mental-health/" target="_blank" rel="noreferrer">
                  NHS mental health support
                </a>
                <a className="sp-link" href="https://www.mind.org.uk/" target="_blank" rel="noreferrer">
                  Mind charity
                </a>
                <a className="sp-link" href="https://www.citizensadvice.org.uk/" target="_blank" rel="noreferrer">
                  Citizens Advice Bureau
                </a>
              </div>
              <p className="text-xs sp-muted mt-3">If you are in immediate danger, contact emergency services.</p>
            </Card>
          </div>

          {portalSettings?.customHtmlEnabled && portalSettings?.customHtml?.trim() && (
            <Card className="p-5">
              <div
                className="text-sm sp-muted"
                dangerouslySetInnerHTML={{ __html: portalSettings.customHtml }}
              />
            </Card>
          )}
        </>
      )}

      {activeTab === 'attendance' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 space-y-3">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Attendance summary</h3>
            <div className="flex items-center gap-2">
              <Badge
                variant={(student?.attendance?.rate || 0) < (settings?.attendanceThreshold || 70) ? 'danger' : 'success'}
              >
                {student?.attendance?.rate || 0}%
              </Badge>
              <span className="text-sm sp-muted">
                {(student?.attendance?.rate || 0) < (settings?.attendanceThreshold || 70) ? 'Below target' : 'On track'}
              </span>
            </div>
            {student?.attendance?.last8Weeks?.length > 0 && (
              <div className="text-xs sp-muted">
                Last 8 weeks: {student.attendance.last8Weeks.join('% · ')}%
              </div>
            )}
            <Button size="sm" variant="secondary" onClick={() => navigate('/student-portal/timetable')}>
              View timetable
            </Button>
          </Card>
          <Card className="p-5 space-y-2">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Absence reasons</h3>
            {(student?.attendance?.absenceReasons || []).length === 0 ? (
              <p className="text-sm sp-muted">No absences recorded.</p>
            ) : (
              student.attendance.absenceReasons.map((reason, idx) => (
                <div key={`${reason.date}-${idx}`} className="text-sm sp-muted">
                  {formatDate(reason.date)} · {reason.reason || 'Recorded'}
                </div>
              ))
            )}
          </Card>
        </div>
      )}

      {activeTab === 'assignments' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 space-y-3">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Assignments</h3>
            {assignments.length === 0 ? (
              <p className="text-sm sp-muted">No assignments yet.</p>
            ) : (
              assignments.map((assignment) => (
                <div key={assignment.id} className="sp-panel sp-panel--muted p-3">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{assignment.title}</div>
                  <div className="text-xs sp-muted">
                    Due {assignment.dueDate ? formatDate(assignment.dueDate) : 'No due date'}
                  </div>
                </div>
              ))
            )}
            <Button size="sm" variant="secondary" onClick={() => navigate('/student-portal/assignments')}>
              Open assignments
            </Button>
          </Card>
          <Card className="p-5 space-y-3">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Upcoming lessons</h3>
            {upcomingLessons.length === 0 ? (
              <p className="text-sm sp-muted">No sessions scheduled yet.</p>
            ) : (
              upcomingLessons.map((lesson) => (
                <div key={lesson.id} className="sp-panel sp-panel--muted p-3">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{lesson.title}</div>
                  <div className="text-xs sp-muted">{lesson.schedule}</div>
                </div>
              ))
            )}
            <Button size="sm" variant="secondary" onClick={() => navigate('/student-portal/timetable')}>
              Open timetable
            </Button>
          </Card>
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 space-y-3">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Recent messages</h3>
            {messages.length === 0 ? (
              <p className="text-sm sp-muted">No messages yet.</p>
            ) : (
              messages.slice(0, 5).map((message) => (
                <div key={message.id} className="sp-panel sp-panel--muted p-3">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{message.subject}</div>
                  <div className="text-xs sp-muted">{formatDate(message.createdAt)} · {message.fromName}</div>
                </div>
              ))
            )}
            <Button size="sm" variant="secondary" onClick={() => navigate('/student-portal/messages')}>
              Open messages
            </Button>
          </Card>
          <Card className="p-5 space-y-3">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Motivation</h3>
            <div className="mt-3">{renderQuote()}</div>
          </Card>
        </div>
      )}

    </div>
  );
}
